import { fabric } from 'fabric';

const multiply = fabric.util.multiplyTransformMatrices;
const invert = fabric.util.invertTransform;

function rotate(cx, cy, x, y, angle) {
  const radians = (Math.PI / 180) * angle;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const nx = cos * (x - cx) + sin * (y - cy) + cx;
  const ny = cos * (y - cy) - sin * (x - cx) + cy;
  return {
    left: nx,
    top: ny,
  };
}

if (fabric.MagicFrame) {
  fabric.warn('fabric.MagicFrame is already defined.');
} else {
  fabric.MagicFrame = fabric.util.createClass(fabric.Image, {
    type: 'MagicFrame',
    originX: 'center',
    originY: 'center',
    erasable: false,
    initialize: function (bg, person, frame, options) {
      if (!options) {
        options = {};
      }
      options = Object.assign(
        {
          left: 0,
          top: 0,
          scaleX: 1,
          scaleY: 1,
          angle: 0,
          width: 433,
          height: 577,
        },
        options
      );
      this.callSuper('initialize', options);
      this.set({
        left: options.left,
        top: options.top,
        scaleX: options.scaleX,
        scaleY: options.scaleY,
        angle: options.angle,
        width: options.width,
        height: options.height,
      });
      this.urls = {
        bg,
        person,
        frame,
      };
      this.isEditing = false;
      this.isErasing = false;
      const promises = [];
      if (this.urls.bg) {
        promises.push(this.loadBackground());
      }
      if (this.urls.person) {
        promises.push(this.loadPerson());
      }
      if (this.urls.frame) {
        promises.push(this.loadFrame());
      }
      Promise.all(promises).then(() => {
        this.loaded();
      });
    },
    getPersonSrc() {
      return this.urls.person;
    },
    getBackgroundSrc() {
      return this.urls.bg;
    },
    getFrameSrc() {
      return this.urls.frame;
    },
    removeBackground() {
      delete this.background;
      delete this.backgroundSVG;
      delete this.urls.bg;
    },
    removeFrame() {
      delete this.frame;
      delete this.frameSVG;
      delete this.urls.frame;
    },
    scalePersonToWidth(width) {
      if (!this.editingImg) {
        return;
      }
      this.editingImg.scaleToWidth(width);
      this.updateProps();
      this.updateRelationship();
    },
    async updatePerson(url) {
      this.urls.person = url;
      // Reset placeholder flag when updating
      this.isPlaceholder = false;
      await this.loadPerson();
      const finalUrl = this.urls.person;

      return new Promise((resolve) => {
        if (!this.editingImg) {
          resolve();
          return;
        }

        this.editingImg.setSrc(
          finalUrl,
          () => {
            // If this is a placeholder, size it to fit the frame
            if (this.isPlaceholder) {
              const frameWidth = this.width;
              const frameHeight = this.height;
              const imgAspectRatio =
                this.editingImg.width / this.editingImg.height;
              const frameAspectRatio = frameWidth / frameHeight;

              let scale;
              if (imgAspectRatio > frameAspectRatio) {
                // Image is wider than frame, fit by height
                scale = frameHeight / this.editingImg.height;
              } else {
                // Image is taller than frame, fit by width
                scale = frameWidth / this.editingImg.width;
              }

              this.editingImg.set({
                scaleX: scale,
                scaleY: scale,
                left: this.left,
                top: this.top,
              });
            } else {
              this.editingImg.set({
                left: this.left,
                top: this.top,
              });
            }
            this.updateProps();
            this.updateRelationship();
            resolve();
          },
          {
            crossOrigin: 'anonymous',
          }
        );
      });
    },
    async updateBackground(url) {
      this.urls.bg = url;
      await this.loadBackground();
    },
    async updateFrame(url) {
      this.urls.frame = url;
      await this.loadFrame();
    },
    loadBackground() {
      return new Promise((resolve) => {
        // Clear previous background representations
        delete this.background;
        delete this.backgroundSVG;
        const src = this.urls.bg || '';
        const isInlineSvg =
          typeof src === 'string' && src.trim().startsWith('<svg');
        const isDataSvg =
          typeof src === 'string' && src.startsWith('data:image/svg');
        const isUrlSvg =
          typeof src === 'string' &&
          src.toLowerCase().split('?')[0].endsWith('.svg');

        const onSvgLoaded = (objects, options) => {
          const group = fabric.util.groupSVGElements(objects, options);
          group.set({
            originX: 'left',
            originY: 'top',
            left: 0,
            top: 0,
            excludeFromExport: true,
            evented: false,
            selectable: false,
          });
          this.backgroundSVG = group;
          this._bgSvgOriginalWidth = group.width || this.width;
          this._bgSvgOriginalHeight = group.height || this.height;
          this.bgLoaded = true;
          this.fire('background:loaded');
          resolve();
        };

        if (isInlineSvg) {
          fabric.loadSVGFromString(src, onSvgLoaded);
        } else if (isDataSvg) {
          try {
            const commaIndex = src.indexOf(',');
            const payload = src.slice(commaIndex + 1);
            const isBase64 = /;base64/i.test(src.substring(0, commaIndex));
            const svgText = isBase64
              ? atob(payload)
              : decodeURIComponent(payload);
            fabric.loadSVGFromString(svgText, onSvgLoaded);
          } catch (e) {
            // Fallback to raster image
            this._loadBackgroundAsImage(resolve);
          }
        } else if (isUrlSvg) {
          fabric.loadSVGFromURL(src, onSvgLoaded, null, {
            crossOrigin: 'anonymous',
          });
        } else {
          this._loadBackgroundAsImage(resolve);
        }
      });
    },
    _loadBackgroundAsImage(resolve) {
      this.background = new Image();
      this.background.crossOrigin = 'anonymous';
      this.background.src = this.urls.bg;
      this.background.onload = function (event) {
        this.backgroundWidth = event.target.width;
        this.backgroundHeight = event.target.height;
        this.bgLoaded = true;
        this.fire('background:loaded');
        resolve();
      }.bind(this);
    },
    loadFrame() {
      return new Promise((resolve) => {
        // Clear previous frame representations so rendering switches correctly
        delete this.frameSVG;
        delete this.frame;
        const src = this.urls.frame || '';
        const isInlineSvg =
          typeof src === 'string' && src.trim().startsWith('<svg');
        const isDataSvg =
          typeof src === 'string' && src.startsWith('data:image/svg');
        const isUrlSvg =
          typeof src === 'string' &&
          src.toLowerCase().split('?')[0].endsWith('.svg');

        if (isInlineSvg || isDataSvg || isUrlSvg) {
          const onLoaded = (objects, options) => {
            const group = fabric.util.groupSVGElements(objects, options);
            group.set({
              originX: 'left',
              originY: 'top',
              left: 0,
              top: 0,
              excludeFromExport: true,
              evented: false,
              selectable: false,
            });
            this.frameSVG = group;
            this._frameSvgOriginalWidth = group.width || this.width;
            this._frameSvgOriginalHeight = group.height || this.height;
            this.frameLoaded = true;
            this.fire('frame:loaded');
            resolve();
          };

          if (isInlineSvg) {
            fabric.loadSVGFromString(src, onLoaded);
          } else if (isDataSvg) {
            try {
              const commaIndex = src.indexOf(',');
              const payload = src.slice(commaIndex + 1);
              const isBase64 = /;base64/i.test(src.substring(0, commaIndex));
              const svgText = isBase64
                ? atob(payload)
                : decodeURIComponent(payload);
              fabric.loadSVGFromString(svgText, onLoaded);
            } catch (e) {
              this._loadFrameAsImage(resolve);
            }
          } else {
            fabric.loadSVGFromURL(src, onLoaded, null, {
              crossOrigin: 'anonymous',
            });
          }
        } else {
          this._loadFrameAsImage(resolve);
        }
      });
    },
    _loadFrameAsImage(resolve) {
      this.frame = new Image();
      this.frame.crossOrigin = 'anonymous';
      this.frame.src = this.urls.frame;
      this.frame.onload = function () {
        this.frameLoaded = true;
        this.fire('frame:loaded');
        resolve();
      }.bind(this);
    },
    loadPerson() {
      return new Promise((resolve) => {
        this.person = new Image();
        this.person.crossOrigin = 'anonymous';
        this.person.src = this.urls.person;
        this.person.onload = function () {
          this.personLoaded = true;
          this.fire('person:loaded');
          resolve();
        }.bind(this);
        this.person.onerror = function () {
          // Use placeholder image as fallback
          const placeholderUrl =
            'https://cdn.onoranzefunebricloud.com/giotto/placeholder/magicframe/man.png';
          this.urls.person = placeholderUrl;
          this.isPlaceholder = true; // Mark that we're using a placeholder
          this.person = new Image();
          this.person.crossOrigin = 'anonymous';
          this.person.src = placeholderUrl;
          this.person.onload = function () {
            this.personLoaded = true;
            this.fire('person:loaded');
            resolve();
          }.bind(this);
          this.person.onerror = function () {
            this.personLoaded = false;
            resolve();
          }.bind(this);
        }.bind(this);
      });
    },
    loaded() {
      this.is_loading = true;
      // If we cloned, this is already filled in
      if (!this.personImageProperties) {
        this.personImageProperties = {
          left: this.left,
          top: this.top,
          width: this.width,
          height: this.height,
          angle: 0,
          scaleX: this.scaleX,
          scaleY: this.scaleY,
          flipX: this.flipX,
          flipY: this.flipY,
        };
      }
      this.setCoords();

      // Skip loading person image only if it completely failed (even placeholder failed)
      if (!this.urls.person || !this.personLoaded) {
        this.is_loading = false;
        this.fire('frame:init', this);
        return;
      }

      fabric.Image.fromURL(
        this.urls.person,
        (img) => {
          // Check if image loaded successfully
          if (!img || img.width === 0 || img.height === 0) {
            this.is_loading = false;
            this.fire('frame:init', this);
            return;
          }

          this.editingImg = img;
          this.editingImg._magicFrame = this;

          // If this is a placeholder, size it to fit the frame
          if (this.isPlaceholder) {
            const frameWidth = this.width;
            const frameHeight = this.height;
            const imgAspectRatio = img.width / img.height;
            const frameAspectRatio = frameWidth / frameHeight;

            let scale;
            if (imgAspectRatio > frameAspectRatio) {
              // Image is wider than frame, fit by height
              scale = frameHeight / img.height;
            } else {
              // Image is taller than frame, fit by width
              scale = frameWidth / img.width;
            }

            img.set({
              scaleX: scale,
              scaleY: scale,
              left: this.left,
              top: this.top,
            });

            // Update the person properties to match
            this.personImageProperties = {
              ...this.personImageProperties,
              scaleX: scale,
              scaleY: scale,
              left: this.left,
              top: this.top,
            };
          }

          let filters = [];
          if (this.editingImgProps) {
            filters = this.editingImgProps.filters || [];
          }
          img.set({
            erasable: false,
            excludeFromExport: true,
          });
          if (filters.length > 0) {
            img._initFilters(filters, (initializedFilters) => {
              img.filters = initializedFilters;
              img.applyFilters();
              this.bindEvents();
              this.is_loading = false;
              this.fire('frame:init', this);
            });
          } else {
            this.bindEvents();
            this.is_loading = false;
            this.fire('frame:init', this);
          }
        },
        this.initialEditingImageProps()
      );
    },
    initialEditingImageProps() {
      return {
        opacity: 0,
        crossOrigin: 'anonymous',
        originX: 'center',
        originY: 'center',
        left: this.editingImgProps
          ? this.editingImgProps.left
          : this.personImageProperties.left,
        top: this.editingImgProps
          ? this.editingImgProps.top
          : this.personImageProperties.top,
        // width: this.editingImgProps
        //     ? this.editingImgProps.width
        //     : this.personImageProperties.width,
        // height: this.editingImgProps
        //     ? this.editingImgProps.height
        //     : this.personImageProperties.height,
        scaleX: this.editingImgProps
          ? this.editingImgProps.scaleX
          : this.personImageProperties.scaleX,
        scaleY: this.editingImgProps
          ? this.editingImgProps.scaleY
          : this.personImageProperties.scaleY,
        angle: this.editingImgProps
          ? this.editingImgProps.angle
          : this.personImageProperties.angle,
        flipX: this.editingImgProps
          ? this.editingImgProps.flipX
          : this.personImageProperties.flipX,
        flipY: this.editingImgProps
          ? this.editingImgProps.flipY
          : this.personImageProperties.flipY,
        evented: false,
        selectable: false,
      };
    },
    updateRelationship() {
      if (!this.editingImg) {
        return;
      }
      var transform = this.calcTransformMatrix();
      var invertedFrameTransform = invert(transform);
      this.editingImg.relationship = multiply(
        invertedFrameTransform,
        this.editingImg.calcTransformMatrix()
      );
    },
    destroy() {
      if (this.editingImg && this.canvas) {
        this.canvas.remove(this.editingImg);
      }
    },
    bindEvents() {
      if (!this.editingImg) {
        return;
      }
      this.updateRelationship();
      ['moving', 'scaling', 'rotating', 'mouseup'].map((eventName) => {
        this.on(eventName, () => {
          this.updateLinked();
        });
        this.editingImg.on(eventName, () => {
          this.updateProps();
        });
      });
    },
    updateLinked() {
      if (!this.editingImg || !this.editingImg.relationship) {
        return;
      }
      var newTransform = multiply(
        this.calcTransformMatrix(),
        this.editingImg.relationship
      );
      var opt = fabric.util.qrDecompose(newTransform);
      this.editingImg.set({
        flipX: false,
        flipY: false,
      });
      this.editingImg.setPositionByOrigin(
        {
          x: opt.translateX,
          y: opt.translateY,
        },
        'center',
        'center'
      );
      this.editingImg.set(opt);
      this.editingImg.setCoords();
      // Ensure stored props reflect latest relationship after syncing
      this.updateProps();
    },
    clone({ withOffsetX = 0, withOffsetY = 0 }) {
      const newFrame = new fabric.MagicFrame(
        this.urls.bg,
        this.urls.person,
        this.urls.frame,
        {
          width: this.width,
          height: this.height,
        }
      );

      newFrame.editingImg = this.editingImg;
      if (this.editingImg) {
        newFrame.setEditingImgProps({
          ...this.personImageProperties,
          scaleX: this.editingImg.scaleX,
          scaleY: this.editingImg.scaleY,
        });
      }
      newFrame.shadow = this.shadow;
      newFrame.clipping = this.clipping;
      newFrame.personImageProperties = this.personImageProperties;

      newFrame.on('frame:init', () => {
        newFrame.set({
          top: this.top + withOffsetX,
          left: this.left + withOffsetY,
          angle: this.angle,
          scaleX: this.scaleX,
          scaleY: this.scaleY,
          flipX: this.flipX,
          flipY: this.flipY,
          filters: this.filters,
          translateX: this.translateX,
          translateY: this.translateY,
          opacity: this.opacity,
        });

        newFrame.canvas.renderAll();
      });

      return newFrame;
    },
    toggleEditMode() {
      this.setEditMode(!this.isEditing);
    },
    toggleEraseMode() {
      this.setEraseMode(!this.isErasing);
    },
    setEraseMode(eraseMode) {
      if (!this.editingImg) {
        return;
      }
      this.isErasing = eraseMode;
      this.editingImg.erasable = eraseMode;
      if (this.isErasing) {
        this.editingImg.set({
          opacity: 1.0,
        });
      } else {
        this.editingImg.set({
          opacity: 0.4,
        });
        this.flattenEditingImage();
      }
      this.canvas.renderAll();
    },
    setEditMode(editMode) {
      this.isEditing = editMode;
      this.set({
        lockMovementX: editMode,
        lockMovementY: editMode,
        evented: !editMode,
        selectable: !editMode,
      });
      if (this.isEditing) {
        if (this.editingImg) {
          this.editingImg.set({
            opacity: 0.4,
            evented: true,
            selectable: true,
          });
          this.editingImg.setCoords();
          this.canvas.setActiveObject(this.editingImg);
        }
      } else {
        this.isErasing = false;
        if (this.editingImg) {
          if (this.editingImg.eraser) {
            this.flattenEditingImage();
          }
          // this.updateProps();
          this.editingImg.set({
            evented: false,
            selectable: false,
            opacity: 0,
          });
        }
        this.setCoords();
        this.updateRelationship();
        this.canvas.discardActiveObject();
      }
      this.canvas.renderAll();
      this.fire('editMode:change', this);
    },
    flattenEditingImage() {
      if (!this.editingImg) {
        return;
      }
      const oldOpacity = this.editingImg.opacity;
      this.editingImg.opacity = 1;
      const url = this.editingImg.toDataURL();
      this.editingImg.opacity = oldOpacity;
      this.editingImg.setSrc(url, () => {
        this.urls.person = url;
        this.editingImg.set({
          scaleX: 1,
          scaleY: 1,
        });
        this.updateRelationship();
        this.canvas.renderAll();
      });
    },
    updateProps() {
      if (!this.editingImg) {
        return;
      }
      this.personImageProperties = this.calculatePersonImageProps();
    },
    calculatePersonImageProps() {
      if (!this.editingImg) {
        return this.personImageProperties;
      }
      const rotated = rotate(
        this.left,
        this.top,
        this.editingImg.left,
        this.editingImg.top,
        this.angle
      );
      return {
        ...this.personImageProperties,
        left: rotated.left - this.left,
        top: rotated.top - this.top,
        width: this.editingImg.width,
        height: this.editingImg.height,
        angle: this.editingImg.angle - this.angle,
        // scaleX: this.editingImg.scaleX,
        // scaleY: this.editingImg.scaleY,
      };
    },
    _scaleToHeight: function (sourceWidth, sourceHeight, targetHeight) {
      const aspectRatio = sourceWidth / sourceHeight;
      return targetHeight * aspectRatio;
    },
    _scaleToWidth: function (sourceWidth, sourceHeight, targetWidth) {
      const aspectRatio = sourceHeight / sourceWidth;
      return targetWidth * aspectRatio;
    },
    _render: function (ctx) {
      if (this.group) {
        this.updateLinked();
      }
      this.lastProps = this.personImageProperties;
      ctx.save();
      if (this.clipping) {
        this.clipping._render(ctx);
        ctx.clip();
      }

      if (this.editingImg && !this.editingImg.canvas && this.canvas) {
        this.canvas.add(this.editingImg);
        this.editingImg.canvas = this.canvas;
      }

      if (this.is_loading === false) {
        // Draw background if needed
        if (this.background) {
          if (this.backgroundWidth > this.backgroundHeight) {
            ctx.drawImage(
              this.background,
              -this.width / 2,
              -this.height / 2,
              this._scaleToHeight(
                this.backgroundWidth,
                this.backgroundHeight,
                this.height
              ),
              this.height
            );
          } else if (this.backgroundWidth < this.backgroundHeight) {
            ctx.drawImage(
              this.background,
              -this.width / 2,
              -this.height / 2,
              this.width,
              this._scaleToWidth(
                this.backgroundWidth,
                this.backgroundHeight,
                this.width
              )
            );
          } else {
            ctx.drawImage(
              this.background,
              -this.width / 2,
              -this.height / 2,
              this.width,
              this.height
            );
          }
        } else if (this.backgroundSVG) {
          const ow =
            this._bgSvgOriginalWidth || this.backgroundSVG.width || this.width;
          const oh =
            this._bgSvgOriginalHeight ||
            this.backgroundSVG.height ||
            this.height;
          // mimic bitmap background scaling behavior
          let scale;
          let left;
          let top;
          if (ow > oh) {
            // fit by height
            scale = this.height / oh;
            const scaledW = ow * scale;
            left = -scaledW / 2;
            top = -this.height / 2;
          } else if (ow < oh) {
            // fit by width
            scale = this.width / ow;
            const scaledH = oh * scale;
            left = -this.width / 2;
            top = -scaledH / 2;
          } else {
            // square -> fill exactly
            const sx = this.width / ow;
            const sy = this.height / oh;
            const prevEq = {
              left: this.backgroundSVG.left,
              top: this.backgroundSVG.top,
              scaleX: this.backgroundSVG.scaleX,
              scaleY: this.backgroundSVG.scaleY,
              originX: this.backgroundSVG.originX,
              originY: this.backgroundSVG.originY,
              angle: this.backgroundSVG.angle,
              flipX: this.backgroundSVG.flipX,
              flipY: this.backgroundSVG.flipY,
            };
            this.backgroundSVG.set({
              originX: 'left',
              originY: 'top',
              left: -this.width / 2,
              top: -this.height / 2,
              scaleX: sx,
              scaleY: sy,
              angle: 0,
              flipX: false,
              flipY: false,
            });
            this.backgroundSVG.render(ctx);
            this.backgroundSVG.set(prevEq);
            // continue after render
            scale = null;
          }
          if (scale) {
            const prev = {
              left: this.backgroundSVG.left,
              top: this.backgroundSVG.top,
              scaleX: this.backgroundSVG.scaleX,
              scaleY: this.backgroundSVG.scaleY,
              originX: this.backgroundSVG.originX,
              originY: this.backgroundSVG.originY,
              angle: this.backgroundSVG.angle,
              flipX: this.backgroundSVG.flipX,
              flipY: this.backgroundSVG.flipY,
            };
            this.backgroundSVG.set({
              originX: 'left',
              originY: 'top',
              left: left,
              top: top,
              scaleX: scale,
              scaleY: scale,
              angle: 0,
              flipX: false,
              flipY: false,
            });
            this.backgroundSVG.render(ctx);
            this.backgroundSVG.set(prev);
          }
        }

        // Draw person if needed

        if (this.editingImg) {
          var invertedFrameTransform = invert(this.calcTransformMatrix());
          const m = multiply(
            invertedFrameTransform,
            this.editingImg.calcTransformMatrix()
          );
          ctx.transform(m[0], m[1], m[2], m[3], m[4], m[5]);
          if (!this.isErasing) {
            this.editingImg._render(ctx);
          }

          ctx.restore();
        }

        if (this.frame) {
          ctx.drawImage(
            this.frame,
            -this.width / 2,
            -this.height / 2,
            this.width,
            this.height
          );
        } else if (this.frameSVG) {
          const ow =
            this._frameSvgOriginalWidth || this.frameSVG.width || this.width;
          const oh =
            this._frameSvgOriginalHeight || this.frameSVG.height || this.height;
          const sx = this.width / ow;
          const sy = this.height / oh;
          const prev = {
            left: this.frameSVG.left,
            top: this.frameSVG.top,
            scaleX: this.frameSVG.scaleX,
            scaleY: this.frameSVG.scaleY,
            originX: this.frameSVG.originX,
            originY: this.frameSVG.originY,
            angle: this.frameSVG.angle,
            flipX: this.frameSVG.flipX,
            flipY: this.frameSVG.flipY,
          };
          ctx.save();
          this.frameSVG.set({
            originX: 'left',
            originY: 'top',
            left: -this.width / 2,
            top: -this.height / 2,
            scaleX: sx,
            scaleY: sy,
            angle: 0,
            flipX: false,
            flipY: false,
          });
          this.frameSVG.render(ctx);
          this.frameSVG.set(prev);
          ctx.restore();
        }
      }
    },
    /**
     * Returns a JSON representation of an instance
     * @param {Array} [propertiesToInclude] Any properties that you might want to additionally include in the output
     * @return {Object} JSON
     */
    toJSON: function (propertiesToInclude) {
      // delegate, not alias
      return this.toObject(propertiesToInclude);
    },
    toObject: function (propertiesToInclude) {
      if (this.editingImg) {
        this.editingImgProps = {
          left: this.editingImg.left,
          top: this.editingImg.top,
          width: this.editingImg.width,
          height: this.editingImg.height,
          scaleX: this.editingImg.scaleX,
          scaleY: this.editingImg.scaleY,
          flipX: this.editingImg.flipX,
          flipY: this.editingImg.flipY,
          angle: this.editingImg.angle,
          filters: this.editingImg.toObject().filters,
        };
      }
      propertiesToInclude = propertiesToInclude || [];
      propertiesToInclude.push('clipping');
      propertiesToInclude.push('lastProps');
      propertiesToInclude.push('editingImgProps');
      propertiesToInclude.push('giotto');
      propertiesToInclude.push('shadow');
      propertiesToInclude.push('urls');
      propertiesToInclude.push('opacity');
      return this.callSuper('toObject', propertiesToInclude);
    },
    setEditingImgProps(editingImgProps) {
      this.editingImgProps = editingImgProps;
    },
    set: function (key, value) {
      // Apply changes to the MagicFrame itself.
      this.callSuper('set', key, value);

      // Keep internal bookkeeping up-to-date, but avoid mirroring
      // frame transforms onto the editing image.
      // Mirroring here competes with updateLinked() and can cause
      // perceptible flicker/jumps during resize.
      if (!this.isEditing) {
        this.updateProps();
        this.setCoords();
      }
    },
    isLoading() {
      return this.is_loading;
    },
  });

  fabric.MagicFrame.fromObject = function (object, callback) {
    const newObj = new fabric.MagicFrame(
      object.urls.bg,
      object.urls.person,
      object.urls.frame,
      Object.assign(
        {
          left: object.left,
          top: object.top,
          scaleX: object.scaleX,
          scaleY: object.scaleY,
          width: object.width,
          height: object.height,
          angle: object.angle,
        },
        typeof object.opacity === 'number' ? { opacity: object.opacity } : null
      )
    );
    newObj.giotto = object.giotto;
    newObj.shadow = object.shadow;
    newObj.personImageProperties = object.lastProps;
    newObj.editingImgProps = object.editingImgProps;
    if (object.clipping) {
      fabric.util.enlivenObjects([object.clipping], function (objects) {
        if (objects[0]) {
          newObj.clipping = objects[0];
        }
      });
    }
    // Fabric will freak out if the callback isn't async
    newObj.on('frame:init', function () {
      if (typeof object.opacity === 'number') {
        newObj.set('opacity', object.opacity);
      }
      callback(newObj);
    });
    return newObj;
  };
}
